import { DialogState, DialogTitle, DIALOG_CONTENT_WRAPPER_CSS, withModalDialog } from "~/dialogs/withModalDialog";
import { ICommandArgs, useRegisterCommands } from "~/environment/command.service";
import * as DialogLayout from "~/dialogs/DialogLayout";
import { IRelativeDateTimeOption, RelativeDateTimeSelect } from "~/components/forms/RelativeDateTimeSelect";
import { RelativeDueDate } from "libs/schema";
import { closeDialogCommand } from "~/utils/common-commands";
import { submitFormCommand } from "~/utils/common-commands";
import { useCallback, useMemo, useState } from "react";

export type IEditDefaultRequestDueDateDialogData = RelativeDueDate | undefined;
export type IEditDefaultRequestDueDateDialogReturnData = { selectedDueDate: RelativeDueDate } | void;

export const EditDefaultRequestDueDateDialogState = new DialogState<
  IEditDefaultRequestDueDateDialogData,
  IEditDefaultRequestDueDateDialogReturnData
>();

export const EditDefaultRequestDueDateDialog = withModalDialog({
  dialogState: EditDefaultRequestDueDateDialogState,
  Component({ data }) {
    const [selectedDueDate, setSelectedDueDate] = useState<RelativeDueDate | null>(data || null);

    const onSubmit = useCallback(() => {
      if (selectedDueDate) {
        EditDefaultRequestDueDateDialogState.close({
          selectedDueDate,
        });
      } else {
        EditDefaultRequestDueDateDialogState.close();
      }
    }, [selectedDueDate]);

    useRegisterCommands({
      commands: () => {
        const commands: ICommandArgs[] = [
          closeDialogCommand({
            callback: () => {
              EditDefaultRequestDueDateDialogState.close();
            },
          }),
          submitFormCommand({
            callback: () => onSubmit(),
          }),
        ];

        return commands;
      },
      deps: [onSubmit],
    });

    return (
      <>
        <DialogTitle>
          <h2>Set Default Request Due Date</h2>
        </DialogTitle>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className={DIALOG_CONTENT_WRAPPER_CSS}
        >
          <div className="p-4">
            <p>When you add a due date to a request, this will be the default suggested due date.</p>
          </div>

          <div className="p-4">
            <DueDateSelect selectedDueDate={selectedDueDate} setSelectedDueDate={setSelectedDueDate} />
          </div>

          <DialogLayout.DialogFooter>
            <div className="flex-1" />

            <DialogLayout.DialogSubmitButton onClick={onSubmit} />
          </DialogLayout.DialogFooter>
        </form>

        <DialogLayout.SubmitDialogHint />
      </>
    );
  },
});

/* -----------------------------------------------------------------------------------------------*/

const DueDateSelect = (props: {
  selectedDueDate: RelativeDueDate | null;
  setSelectedDueDate: (dueDate: RelativeDueDate | null) => void;
}) => {
  const value = useMemo((): IRelativeDateTimeOption | null => {
    return props.selectedDueDate ? { label: props.selectedDueDate.content, value: props.selectedDueDate } : null;
  }, [props.selectedDueDate]);

  return (
    <div className="border rounded border-slate-9">
      <RelativeDateTimeSelect
        placeholder="E.g. 2 days from now"
        value={value}
        onChange={(option) => props.setSelectedDueDate(option ? option.value : null)}
      />
    </div>
  );
};

/* -----------------------------------------------------------------------------------------------*/
